import { LogRocket } from '@/lib/logrocket';
import { Sentry } from '@/lib/sentry';

const isDevelopment = import.meta.env.DEV;
const sentryEnabledInDev = import.meta.env.VITE_SENTRY_ENABLE_IN_DEV === 'true';
const sendToServices = !isDevelopment || sentryEnabledInDev;
const sentryCanInstrumentConsole =
  Boolean(import.meta.env.VITE_SENTRY_DSN) && (!isDevelopment || sentryEnabledInDev);
const logRocketCanInstrumentConsole =
  Boolean(import.meta.env.VITE_LOGROCKET_APP_ID) &&
  (!isDevelopment || import.meta.env.VITE_LOGROCKET_ENABLE_IN_DEV === 'true');
const consoleCanBeRemoteTelemetry = sentryCanInstrumentConsole || logRocketCanInstrumentConsole;

function devLog(method: (...args: unknown[]) => void, message: string, options?: object): void {
  if (options) {
    method(message, options);
  } else {
    method(message);
  }
}

interface LogOptions {
  context?: Record<string, unknown>;
}

export interface LogErrorOptions extends LogOptions {
  error?: unknown;
  /** Use console output only when no configured telemetry service can capture it. */
  consoleFallback?: boolean;
  /** Skip development console output when the caller already emitted the same error. */
  skipConsole?: boolean;
}

export interface LogWarnOptions extends LogOptions {
  capture?: boolean;
}

/**
 * Centralized logger for frontend errors, warnings, info, and debug messages.
 *
 * In development:
 * - All levels always log to console
 * - With VITE_SENTRY_ENABLE_IN_DEV=true, error/warn/info also send to Sentry/LogRocket
 * - debug is always console-only (never sent to services)
 *
 * In production and staging:
 * - error: sends to Sentry and LogRocket (no console output)
 * - warn: adds Sentry breadcrumb (no console output)
 * - info: adds Sentry breadcrumb (no console output)
 * - debug: no-op (completely stripped)
 */
export const logger = {
  /**
   * Verbose development-only logging. Never sent to Sentry/LogRocket.
   * No-op in production to avoid console noise and performance overhead.
   */
  debug(message: string, ...args: unknown[]): void {
    if (isDevelopment) {
      console.debug(message, ...args);
    }
  },

  /** Log locally only when no configured telemetry service can capture the console call. */
  localError(message: string, options?: LogErrorOptions): void {
    if (!consoleCanBeRemoteTelemetry) {
      devLog(console.error, message, options);
    }
  },

  error(message: string, options?: LogErrorOptions): void {
    const error = options?.error;
    const context = options?.context;

    const useConsoleFallback = options?.consoleFallback && !consoleCanBeRemoteTelemetry;

    if ((isDevelopment && !options?.skipConsole) || useConsoleFallback) {
      devLog(console.error, message, options);
    }

    if (sendToServices) {
      if (error instanceof Error) {
        Sentry.captureException(error, {
          tags: { loggerMessage: message },
          contexts: context ? { custom: context } : undefined
        });
        LogRocket.captureException(error, {
          tags: { loggerMessage: message },
          extra: context as Record<string, string | number | boolean>
        });
      } else {
        const errorContext = { error, ...context };
        Sentry.captureMessage(message, {
          level: 'error',
          contexts: { custom: errorContext }
        });
        LogRocket.captureMessage(message, {
          extra: errorContext as Record<string, string | number | boolean>
        });
      }
    }
  },

  /**
   * @param options.capture - If true, sends as a standalone Sentry event instead of just a breadcrumb.
   *   Use for warnings that are actionable on their own (e.g., rate limits, deprecated usage).
   */
  warn(message: string, options?: LogWarnOptions): void {
    const context = options?.context;

    if (isDevelopment) {
      devLog(console.warn, message, options);
    }

    if (sendToServices) {
      if (options?.capture) {
        Sentry.captureMessage(message, {
          level: 'warning',
          contexts: context ? { custom: context } : undefined
        });
      } else {
        Sentry.addBreadcrumb({
          level: 'warning',
          message,
          data: context
        });
      }
    }
  },

  info(message: string, options?: LogOptions): void {
    const context = options?.context;

    if (isDevelopment) {
      devLog(console.info, message, options);
    }

    if (sendToServices) {
      Sentry.addBreadcrumb({
        level: 'info',
        message,
        data: context
      });
    }
  }
};
